import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export interface VisibilityRow {
  id: string;
  clientId: string;
  clientName: string | null;
  departmentId: string;
  departmentName: string | null;
  status: "active" | "inactive";
  grantedById: string | null;
  grantedByName: string | null;
  grantedAt: string | null;
  revokedById: string | null;
  revokedByName: string | null;
  revokedAt: string | null;
  createdAt: string | null;
}

export interface ClientDocumentRow {
  id: string;
  clientId: string;
  clientName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  description: string;
  status: "active" | "inactive";
  lifecycle: "linked" | "unlinked" | "other";
  attachmentId: string | null;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  docNo: string;
  uploadedById: string | null;
  uploadedByName: string | null;
  uploadedAt: string | null;
  linkedWorkUnitId: string | null;
  linkedWorkUnitCtrlNo: string | null;
  linkedById: string | null;
  linkedByName: string | null;
  linkedAt: string | null;
  financialYear: string;
  linkedWorkflowDeptName: string | null;
  linkedStepsCompleted: number;
  linkedTotalSteps: number;
  linkedProgressPct: number;
}

interface Paginated<T> {
  results: T[];
  nextCursor: string | null;
  prevCursor: string | null;
}

// ---------- Visibility (admin side) ----------

export function useClientVisibility(clientId: string | null) {
  return useQuery({
    queryKey: ["client-visibility", clientId],
    enabled: !!clientId,
    queryFn: () =>
      api
        .get<Paginated<VisibilityRow>>(`/clients/${clientId}/visibility`)
        .then((r) => r.data.results),
  });
}

export function useGrantClientVisibility(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (departmentId: string) =>
      api
        .post<VisibilityRow>(`/clients/${clientId}/visibility`, { departmentId })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-visibility", clientId] });
    },
  });
}

export function useRevokeClientVisibility(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (visibilityId: string) =>
      api
        .post<VisibilityRow>(`/client-visibility/${visibilityId}/revoke`, {})
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-visibility", clientId] });
    },
  });
}

// ---------- Client portal (client role) ----------

export interface ClientPortalSelfPayload {
  client: { id: string; name: string };
  visibleDepartments: { departmentId: string; departmentName: string }[];
}

export function useClientPortalSelf() {
  return useQuery({
    queryKey: ["client-portal-me"],
    queryFn: () =>
      api.get<ClientPortalSelfPayload>("/client-portal/me").then((r) => r.data),
  });
}

export function useUploadClientDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      file: File;
      departmentId: string | null;
      description: string;
    }) => {
      const fd = new FormData();
      fd.append("file", input.file);
      if (input.departmentId) fd.append("departmentId", input.departmentId);
      if (input.description) fd.append("description", input.description);
      return api
        .post<ClientDocumentRow>("/client-portal/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-portal-my-docs"] });
    },
  });
}

export function useMyClientDocs() {
  return useQuery({
    queryKey: ["client-portal-my-docs"],
    queryFn: () =>
      api
        .get<Paginated<ClientDocumentRow>>("/client-portal/documents")
        .then((r) => r.data.results),
  });
}

// ---------- Admin queue ----------

export interface ClientDocFilters {
  clientId?: string;
  departmentId?: string;
  lifecycle?: "linked" | "unlinked" | "other";
  status?: "active" | "inactive";
  workUnitId?: string;
  search?: string;
}

export interface ClientDocsCounts {
  total: number;
  unrouted: number;
  unlinked: number;
  linked: number;
  actionable: number;
}

export function useClientDocsCounts(enabled = true) {
  return useQuery<ClientDocsCounts>({
    queryKey: ["client-docs", "counts"],
    enabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
    queryFn: () =>
      api.get<ClientDocsCounts>("/client-docs/counts").then((r) => r.data),
  });
}

export function useClientDocs(filters: ClientDocFilters = {}) {
  return useQuery({
    queryKey: ["client-docs", filters],
    queryFn: () =>
      api
        .get<Paginated<ClientDocumentRow>>("/client-docs", { params: filters })
        .then((r) => r.data.results),
  });
}

export function useClientDoc(docId: string | null) {
  return useQuery({
    queryKey: ["client-doc", docId],
    enabled: !!docId,
    queryFn: () =>
      api.get<ClientDocumentRow>(`/client-docs/${docId}`).then((r) => r.data),
  });
}

function invalidateAllClientDocLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["client-docs"] });
  qc.invalidateQueries({ queryKey: ["client-portal-my-docs"] });
  qc.invalidateQueries({ queryKey: ["work-unit-client-docs"] });
}

export function useSetClientDocDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { docId: string; departmentId: string }) =>
      api
        .post<ClientDocumentRow>(
          `/client-docs/${input.docId}/set-department`,
          { departmentId: input.departmentId },
        )
        .then((r) => r.data),
    onSuccess: () => invalidateAllClientDocLists(qc),
  });
}

export function useLinkClientDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { docId: string; workUnitId: string }) =>
      api
        .post<ClientDocumentRow>(`/client-docs/${input.docId}/link`, {
          workUnitId: input.workUnitId,
        })
        .then((r) => r.data),
    onSuccess: () => invalidateAllClientDocLists(qc),
  });
}

export function useUnlinkClientDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) =>
      api
        .post<ClientDocumentRow>(`/client-docs/${docId}/unlink`, {})
        .then((r) => r.data),
    onSuccess: () => invalidateAllClientDocLists(qc),
  });
}

export function useDeactivateClientDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { docId: string; reason?: string }) =>
      api
        .post<ClientDocumentRow>(`/client-docs/${input.docId}/deactivate`, {
          reason: input.reason || "",
        })
        .then((r) => r.data),
    onSuccess: () => invalidateAllClientDocLists(qc),
  });
}

export function useActivateClientDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) =>
      api
        .post<ClientDocumentRow>(`/client-docs/${docId}/activate`, {})
        .then((r) => r.data),
    onSuccess: () => invalidateAllClientDocLists(qc),
  });
}

// ---------- WorkUnit-side embed ----------

export function useWorkUnitClientDocs(workUnitId: string | null) {
  return useQuery({
    queryKey: ["work-unit-client-docs", workUnitId],
    enabled: !!workUnitId,
    queryFn: () =>
      api
        .get<Paginated<ClientDocumentRow>>(
          `/work-units/${workUnitId}/client-documents`,
        )
        .then((r) => r.data.results),
  });
}
